import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import { api } from '../api';
import { t, formatDate } from '../i18n';
import { Select, Spinner, SectionTitle, EmptyState, Button } from '../ui';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend, zoomPlugin);

const RANGES = [
    { value: 7, label: 'stats_7_derniers_jours' },
    { value: 30, label: 'stats_30_derniers_jours' },
    { value: 90, label: 'stats_90_derniers_jours' },
    { value: 365, label: 'stats_12_derniers_mois' },
];

/**
 * Série par événement : libellé (clé gettext existante du tunnel) + couleur.
 * share_start et add_favorite sont volontairement exclus de l'interface.
 */
const EVENT_META = [
    { key: 'place_view', label: 'funnel_vues', color: '#165DD7' },
    { key: 'contact_view', label: 'funnel_contact_ouvert', color: '#27D9A1' },
    { key: 'phone_start', label: 'funnel_tel', color: '#F59E0B' },
    { key: 'email_start', label: 'funnel_email', color: '#8B5CF6' },
    { key: 'website_start', label: 'funnel_site', color: '#EC4899' },
    { key: 'directions_start', label: 'funnel_itineraires', color: '#0EA5E9' },
];

function Step({ value, label, sub }) {
    return (
        <div className="funnel-step">
            <div className="funnel-step__value">{value.toLocaleString()}</div>
            <div className="text-sm" style={{ fontWeight: 'var(--fw-bold)' }}>{label}</div>
            {sub && <div className="text-muted text-sm">{sub}</div>}
        </div>
    );
}

function Arrow() {
    return <div className="funnel-arrow">→</div>;
}

function ChannelTag({ label, value, variant = 'blue' }) {
    return (
        <span className={`tag tag--${variant}`} style={{ fontSize: 'var(--text-sm)', padding: '6px 12px' }}>
            {label} : <strong style={{ marginLeft: 4 }}>{value.toLocaleString()}</strong>
        </span>
    );
}

/** Carte d'un tunnel de conversion (vues → contacts, vues → itinéraires…). */
function ConversionCard({ title, children }) {
    return (
        <div style={{ border: '1px solid var(--border-1)', borderRadius: 'var(--radius-lg)', padding: 16 }}>
            <div className="text-sm" style={{ fontWeight: 'var(--fw-bold)', marginBottom: 16 }}>{title}</div>
            {children}
        </div>
    );
}

/**
 * Panneau de stats d'un lieu : graph quotidien multi-événements (données
 * BigQuery, endpoint premium.place.stats.events) + tunnel vues -> contacts.
 *
 * Interactions :
 *  - légende cliquable : afficher/masquer chaque événement ;
 *  - glisser sur le graph : zoom sur la période sélectionnée ;
 *  - le tunnel sous le graph est recalculé sur la plage visible.
 */
export default function StatsPanel({ placeId }) {
    const [days, setDays] = useState(30);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    // Plage visible après zoom : [indexMin, indexMax] dans stats.data, ou null
    const [range, setRange] = useState(null);
    const chartRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setRange(null);
        api.statsEvents(placeId, days)
            .then((data) => { if (!cancelled) setStats(data); })
            .catch(() => { if (!cancelled) setStats(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [placeId, days]);

    const rows = stats ? stats.data : [];
    const visibleRows = useMemo(() => {
        if (!range) return rows;
        return rows.slice(range[0], range[1] + 1);
    }, [rows, range]);

    // Agrégats du tunnel sur la plage visible
    const totals = useMemo(() => {
        const sums = Object.fromEntries(EVENT_META.map((m) => [m.key, 0]));
        visibleRows.forEach((row) => {
            EVENT_META.forEach((m) => { sums[m.key] += row[m.key] || 0; });
        });
        return sums;
    }, [visibleRows]);

    const contacts = totals.phone_start + totals.email_start + totals.website_start;
    const toRate = (n) => (totals.place_view > 0 ? Math.round((n / totals.place_view) * 1000) / 10 : 0);
    const contactsRate = toRate(contacts);
    const directionsRate = toRate(totals.directions_start);

    const resetZoom = () => {
        chartRef.current?.resetZoom();
        setRange(null);
    };

    const onZoomComplete = ({ chart }) => {
        const { min, max } = chart.scales.x;
        const last = rows.length - 1;
        const from = Math.max(0, Math.round(min));
        const to = Math.min(last, Math.round(max));
        setRange(from === 0 && to === last ? null : [from, to]);
    };

    return (
        <div>
            {/* ── Graph ─────────────────────────────────────── */}
            <div className="card__header">
                <div>
                    <SectionTitle>{t('stats_vues')}</SectionTitle>
                    {stats && stats.has_data && (
                        <p className="page-subtitle">
                            <span className="stat-value">{totals.place_view.toLocaleString()}</span>{' '}
                            {t('stats_total_vues')}
                        </p>
                    )}
                </div>
                <div className="toolbar" style={{ flexWrap: 'nowrap' }}>
                    {range && (
                        <Button variant="secondary" size="sm" onClick={resetZoom}>
                            {t('stats_zoom_reset')}
                        </Button>
                    )}
                    <Select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 'auto' }}>
                        {RANGES.map((r) => (
                            <option key={r.value} value={r.value}>{t(r.label)}</option>
                        ))}
                    </Select>
                </div>
            </div>

            {loading && <Spinner />}
            {!loading && (!stats || !stats.has_data) && <EmptyState>{t('stats_aucune_donnee')}</EmptyState>}
            {!loading && stats && stats.has_data && (
                <>
                    <div style={{ height: 260 }}>
                        <Line
                            ref={chartRef}
                            data={{
                                labels: rows.map((d) => d.date),
                                datasets: EVENT_META.map((m, i) => ({
                                    label: t(m.label),
                                    data: rows.map((d) => d[m.key] || 0),
                                    borderColor: m.color,
                                    backgroundColor: i === 0 ? 'rgba(22, 93, 215, 0.08)' : m.color,
                                    fill: i === 0,
                                    tension: 0.3,
                                    pointRadius: rows.length > 60 ? 0 : 2,
                                    pointHoverRadius: 4,
                                    borderWidth: 2,
                                })),
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                interaction: { mode: 'index', intersect: false },
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'bottom',
                                        labels: { usePointStyle: true, pointStyle: 'circle', boxHeight: 6 },
                                    },
                                    tooltip: {
                                        callbacks: {
                                            title: (items) => formatDate(items[0].label, {
                                                day: 'numeric', month: 'long', year: 'numeric',
                                            }),
                                        },
                                    },
                                    zoom: {
                                        limits: { x: { min: 'original', max: 'original' } },
                                        zoom: {
                                            drag: {
                                                enabled: true,
                                                backgroundColor: 'rgba(22, 93, 215, 0.12)',
                                            },
                                            mode: 'x',
                                            onZoomComplete,
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        grid: { display: false },
                                        ticks: {
                                            maxTicksLimit: 8,
                                            maxRotation: 0,
                                            callback(value) {
                                                return formatDate(this.getLabelForValue(value), {
                                                    day: '2-digit', month: '2-digit',
                                                });
                                            },
                                        },
                                    },
                                    y: { beginAtZero: true, ticks: { precision: 0 } },
                                },
                            }}
                        />
                    </div>
                    <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>{t('stats_zoom_aide')}</p>

                    {/* ── Conversions sur la plage visible ─────── */}
                    <div style={{ marginTop: 24 }}>
                        <div className="section-head">
                            <SectionTitle>{t('stats_conversions')}</SectionTitle>
                            {visibleRows.length > 0 && (
                                <span className="text-muted text-sm">
                                    {t('du')} {formatDate(visibleRows[0].date)} {t('au')} {formatDate(visibleRows[visibleRows.length - 1].date)}
                                </span>
                            )}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
                            <ConversionCard title={t('funnel_titre')}>
                                <div className="funnel-steps" style={{ marginBottom: 16 }}>
                                    <Step value={totals.place_view} label={t('funnel_vues')} />
                                    <Arrow />
                                    <Step value={totals.contact_view} label={t('funnel_contact_ouvert')} />
                                    <Arrow />
                                    <Step
                                        value={contacts}
                                        label={t('funnel_contacts')}
                                        sub={`${contactsRate}% ${t('funnel_conversion')}`}
                                    />
                                </div>
                                <div className="toolbar" style={{ gap: 8 }}>
                                    <ChannelTag label={t('funnel_tel')} value={totals.phone_start} />
                                    <ChannelTag label={t('funnel_email')} value={totals.email_start} />
                                    <ChannelTag label={t('funnel_site')} value={totals.website_start} />
                                </div>
                            </ConversionCard>

                            <ConversionCard title={t('funnel_titre_itineraires')}>
                                <div className="funnel-steps">
                                    <Step value={totals.place_view} label={t('funnel_vues')} />
                                    <Arrow />
                                    <Step
                                        value={totals.directions_start}
                                        label={t('funnel_itineraires')}
                                        sub={`${directionsRate}% ${t('funnel_conversion')}`}
                                    />
                                </div>
                            </ConversionCard>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
